import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft,
  Users, 
  Calendar, 
  MapPin, 
  DollarSign,
  Edit,
  UserPlus,
  BookOpen,
  FileText,
  Clock,
  User,
  Loader,
  Video,
  Megaphone,
  MessageCircle,
  Award,
  Plus,
  Trash2,
  Download,
  X,
  Save,
  Upload,
  Send
} from 'lucide-react';
import { db } from '../../firebase/config';
import { 
  doc,
  getDoc,
  collection, 
  query, 
  where, 
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp
} from 'firebase/firestore';
import { useRealtime } from '../../contexts/RealtimeContext';
import BatchEnrolledStudents from '../../components/coaching/BatchEnrolledStudents';
import BatchStudentEnrollment from '../../components/coaching/BatchStudentEnrollment';
import { 
  addBatchClass,
  updateBatchClass,
  deleteBatchClass
} from '../../firebase/collections';
import { uploadFile } from '../../firebase/r2storage';
import { isCoachingAdmin as checkIsCoachingAdmin } from '../../utils/rbac';
import toast from 'react-hot-toast';

const BatchDetailsPage = () => {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const { currentUser, userProfile } = useRealtime();
  
  const isCoachingAdmin = checkIsCoachingAdmin(userProfile, currentUser);
  
  const [activeTab, setActiveTab] = useState('overview');
  const [batch, setBatch] = useState(null);
  const [enrolledStudents, setEnrolledStudents] = useState([]);
  const [batchClasses, setBatchClasses] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [doubts, setDoubts] = useState([]);
  const [tests, setTests] = useState([]);
  const [timetables, setTimetables] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEnrollmentModal, setShowEnrollmentModal] = useState(false);
  const [showStudentsModal, setShowStudentsModal] = useState(false);
  
  // Modal states
  const [showClassModal, setShowClassModal] = useState(false);
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [showTimetableModal, setShowTimetableModal] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [showDoubtModal, setShowDoubtModal] = useState(false);
  
  // Editing states
  const [editingClass, setEditingClass] = useState(null);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [editingTimetable, setEditingTimetable] = useState(null);
  const [editingResult, setEditingResult] = useState(null);
  
  // Form data states
  const [classFormData, setClassFormData] = useState({
    subject: '',
    date: '',
    time: '',
    duration: 60,
    zoomLink: '',
    status: 'scheduled',
    description: ''
  });
  
  const [announcementFormData, setAnnouncementFormData] = useState({
    title: '',
    content: ''
  });
  
  const [testFormData, setTestFormData] = useState({
    testId: '',
    testTitle: ''
  });
  
  const [timetableFormData, setTimetableFormData] = useState({
    title: '',
    description: '',
    pdfUrl: ''
  });
  
  const [resultFormData, setResultFormData] = useState({
    studentEmail: '',
    studentName: '',
    examName: '',
    examDate: '',
    documentUrl: '',
    documentType: 'result'
  });
  
  const [doubtAnswer, setDoubtAnswer] = useState('');
  const [answeringDoubt, setAnsweringDoubt] = useState(null);
  
  // Upload states
  const [uploadingRecording, setUploadingRecording] = useState(false);
  const [uploadingTimetable, setUploadingTimetable] = useState(false);
  const [uploadingResult, setUploadingResult] = useState(false);

  const coachingId = userProfile?.coachingId || null;

  useEffect(() => {
    loadBatchData();
  }, [batchId]);

  const loadBatchData = async () => {
    try {
      setLoading(true);
      
      // Load batch details
      const batchDoc = await getDoc(doc(db, 'batches', batchId));
      if (!batchDoc.exists()) {
        toast.error('Batch not found');
        navigate('/coaching-admin/batches');
        return;
      }
      
      setBatch({ id: batchDoc.id, ...batchDoc.data() });
      
      // Load all batch data
      // eslint-disable-next-line no-use-before-define
      await Promise.all([
        loadEnrolledStudents(),
        loadBatchClasses(),
        loadAnnouncements(),
        loadDoubts(),
        loadTests(),
        loadTimetables(),
        loadResults()
      ]);
    } catch (error) {
      console.error('Error loading batch data:', error);
      toast.error('Failed to load batch details');
    } finally {
      setLoading(false);
    }
  };

  const loadEnrolledStudents = async () => {
    try {
      const enrollmentsRef = collection(db, 'batchEnrollments');
      const enrollmentsQuery = query(enrollmentsRef, where('batchId', '==', batchId));
      const enrollmentsSnapshot = await getDocs(enrollmentsQuery);
      
      const enrollmentsData = await Promise.all(
        enrollmentsSnapshot.docs.map(async (enrollDoc) => {
          const enrollmentData = enrollDoc.data();
          
          // If name is missing, try to fetch from students collection
          if (!enrollmentData.name && enrollmentData.email) {
            try {
              const studentsRef = collection(db, 'students');
              const studentQuery = query(
                studentsRef, 
                where('email', '==', enrollmentData.email.toLowerCase())
              );
              const studentSnapshot = await getDocs(studentQuery);
              
              if (!studentSnapshot.empty) {
                const studentData = studentSnapshot.docs[0].data();
                return {
                  id: enrollDoc.id,
                  ...enrollmentData,
                  name: studentData.name || 'Unknown User'
                };
              }
            } catch (error) {
              console.error('Error fetching student name:', error);
            }
          }
          
          return {
            id: enrollDoc.id,
            ...enrollmentData,
            name: enrollmentData.name || 'Unknown User'
          };
        })
      );
      
      setEnrolledStudents(enrollmentsData);
    } catch (error) {
      console.error('Error loading enrolled students:', error);
    }
  };

  const loadBatchClasses = async () => {
    try {
      const classesRef = collection(db, 'batchClasses');
      const classesQuery = query(classesRef, where('batchId', '==', batchId));
      const classesSnapshot = await getDocs(classesQuery);
      
      const classesData = classesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setBatchClasses(classesData);
    } catch (error) {
      console.error('Error loading classes:', error);
    }
  };

  const loadAnnouncements = async () => {
    try {
      const announcementsRef = collection(db, 'batchAnnouncements');
      const announcementsQuery = query(announcementsRef, where('batchId', '==', batchId));
      const announcementsSnapshot = await getDocs(announcementsQuery);
      
      const announcementsData = announcementsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setAnnouncements(announcementsData);
    } catch (error) {
      console.error('Error loading announcements:', error);
    }
  };

  const loadDoubts = async () => {
    try {
      const doubtsRef = collection(db, 'batchDoubts');
      const doubtsQuery = query(doubtsRef, where('batchId', '==', batchId));
      const doubtsSnapshot = await getDocs(doubtsQuery);
      
      const doubtsData = doubtsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setDoubts(doubtsData);
    } catch (error) {
      console.error('Error loading doubts:', error);
    }
  };

  const loadTests = async () => {
    try {
      const testsRef = collection(db, 'batchTests');
      const testsQuery = query(testsRef, where('batchId', '==', batchId));
      const testsSnapshot = await getDocs(testsQuery);
      
      const testsData = testsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setTests(testsData);
    } catch (error) {
      console.error('Error loading tests:', error);
    }
  };

  const loadTimetables = async () => {
    try {
      const timetablesRef = collection(db, 'timetables');
      const timetablesQuery = query(timetablesRef, where('batchId', '==', batchId));
      const timetablesSnapshot = await getDocs(timetablesQuery);
      
      const timetablesData = timetablesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setTimetables(timetablesData);
    } catch (error) {
      console.error('Error loading timetables:', error);
    }
  };

  const loadResults = async () => {
    try {
      const resultsRef = collection(db, 'batchResults');
      const resultsQuery = query(resultsRef, where('batchId', '==', batchId));
      const resultsSnapshot = await getDocs(resultsQuery);
      
      const resultsData = resultsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setResults(resultsData);
    } catch (error) {
      console.error('Error loading results:', error);
    }
  };

  // Handler functions
  const handleClassSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!classFormData.subject.trim() || !classFormData.date || !classFormData.time) {
        toast.error('Subject, date, and time are required');
        return;
      }

      const classData = {
        ...classFormData,
        batchId: batchId,
        date: new Date(classFormData.date).toISOString().split('T')[0],
        duration: parseInt(classFormData.duration) || 60
      };

      if (editingClass) {
        const result = await updateBatchClass(editingClass.id, classData);
        if (result.success) {
          toast.success('Class updated successfully');
          setShowClassModal(false);
          setEditingClass(null);
          await loadBatchClasses();
        }
      } else {
        const result = await addBatchClass(classData);
        if (result.success) {
          toast.success('Class added successfully');
          setShowClassModal(false);
          await loadBatchClasses();
        }
      }
      setClassFormData({
        subject: '',
        date: '',
        time: '',
        duration: 60,
        zoomLink: '',
        status: 'scheduled',
        description: ''
      });
    } catch (error) {
      toast.error('Failed to save class');
    }
  };

  const handleDeleteClass = async (classId) => {
    if (window.confirm('Are you sure you want to delete this class?')) {
      try {
        const result = await deleteBatchClass(classId);
        if (result.success) {
          toast.success('Class deleted successfully');
          await loadBatchClasses();
        }
      } catch (error) {
        toast.error('Failed to delete class');
      }
    }
  };

  const handleAnnouncementSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!announcementFormData.title.trim() || !announcementFormData.content.trim()) {
        toast.error('Title and content are required');
        return;
      }

      const announcementData = {
        ...announcementFormData,
        batchId: batchId,
        createdByName: userProfile?.name || 'Admin',
        createdAt: serverTimestamp()
      };

      if (editingAnnouncement) {
        await updateDoc(doc(db, 'batchAnnouncements', editingAnnouncement.id), announcementData);
        toast.success('Announcement updated successfully');
      } else {
        await addDoc(collection(db, 'batchAnnouncements'), announcementData);
        toast.success('Announcement added successfully');
      }
      
      setShowAnnouncementModal(false);
      setEditingAnnouncement(null);
      setAnnouncementFormData({ title: '', content: '' });
      await loadAnnouncements();
    } catch (error) {
      toast.error('Failed to save announcement');
    }
  };

  const handleDeleteAnnouncement = async (announcementId) => {
    if (window.confirm('Are you sure you want to delete this announcement?')) {
      try {
        await deleteDoc(doc(db, 'batchAnnouncements', announcementId));
        toast.success('Announcement deleted successfully');
        await loadAnnouncements();
      } catch (error) {
        toast.error('Failed to delete announcement');
      }
    }
  };

  const handleTestSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!testFormData.testId.trim()) {
        toast.error('Test ID is required');
        return;
      }

      const testData = {
        ...testFormData,
        batchId: batchId,
        addedByName: userProfile?.name || 'Admin',
        addedAt: serverTimestamp()
      };

      await addDoc(collection(db, 'batchTests'), testData);
      toast.success('Test added successfully');
      setShowTestModal(false);
      setTestFormData({ testId: '', testTitle: '' });
      await loadTests();
    } catch (error) {
      toast.error('Failed to add test');
    }
  };

  const handleDeleteTest = async (testId) => {
    if (window.confirm('Are you sure you want to remove this test?')) {
      try {
        await deleteDoc(doc(db, 'batchTests', testId));
        toast.success('Test removed successfully');
        await loadTests();
      } catch (error) {
        toast.error('Failed to remove test');
      }
    }
  };

  const handleTimetableSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!timetableFormData.title.trim()) {
        toast.error('Title is required');
        return;
      }

      const timetableData = {
        ...timetableFormData,
        batchId: batchId,
        createdAt: serverTimestamp()
      };

      if (editingTimetable) {
        await updateDoc(doc(db, 'timetables', editingTimetable.id), timetableData);
        toast.success('Timetable updated successfully');
      } else {
        await addDoc(collection(db, 'timetables'), timetableData);
        toast.success('Timetable added successfully');
      }
      
      setShowTimetableModal(false);
      setEditingTimetable(null);
      setTimetableFormData({ title: '', description: '', pdfUrl: '' });
      await loadTimetables();
    } catch (error) {
      toast.error('Failed to save timetable');
    }
  };

  const handleDeleteTimetable = async (timetableId) => {
    if (window.confirm('Are you sure you want to delete this timetable?')) {
      try {
        await deleteDoc(doc(db, 'timetables', timetableId));
        toast.success('Timetable deleted successfully');
        await loadTimetables();
      } catch (error) {
        toast.error('Failed to delete timetable');
      }
    }
  };

  const handleResultSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!resultFormData.studentName.trim() || !resultFormData.examName.trim()) {
        toast.error('Student name and exam name are required');
        return;
      }

      const resultData = {
        ...resultFormData,
        batchId: batchId,
        createdAt: serverTimestamp()
      };

      if (editingResult) {
        await updateDoc(doc(db, 'batchResults', editingResult.id), resultData);
        toast.success('Result updated successfully');
      } else {
        await addDoc(collection(db, 'batchResults'), resultData);
        toast.success('Result added successfully');
      }
      
      setShowResultModal(false);
      setEditingResult(null);
      setResultFormData({
        studentEmail: '',
        studentName: '',
        examName: '',
        examDate: '',
        documentUrl: '',
        documentType: 'result'
      });
      await loadResults();
    } catch (error) {
      toast.error('Failed to save result');
    }
  };

  const handleDeleteResult = async (resultId) => {
    if (window.confirm('Are you sure you want to delete this result?')) {
      try {
        await deleteDoc(doc(db, 'batchResults', resultId));
        toast.success('Result deleted successfully');
        await loadResults();
      } catch (error) {
        toast.error('Failed to delete result');
      }
    }
  };

  const handleAnswerDoubt = async (doubtId) => {
    try {
      if (!doubtAnswer.trim()) {
        toast.error('Please enter an answer');
        return;
      }

      await updateDoc(doc(db, 'batchDoubts', doubtId), {
        answer: doubtAnswer,
        status: 'answered',
        answeredByName: userProfile?.name || 'Admin',
        answeredAt: serverTimestamp()
      });

      toast.success('Doubt answered successfully');
      setShowDoubtModal(false);
      setAnsweringDoubt(null);
      setDoubtAnswer('');
      await loadDoubts();
    } catch (error) {
      toast.error('Failed to answer doubt');
    }
  };

  const handleCloseDoubt = async (doubtId) => {
    try {
      await updateDoc(doc(db, 'batchDoubts', doubtId), {
        status: 'closed'
      });
      toast.success('Doubt closed successfully');
      await loadDoubts();
    } catch (error) {
      toast.error('Failed to close doubt');
    }
  };

  const handleDeleteDoubt = async (doubtId) => {
    if (window.confirm('Are you sure you want to delete this doubt?')) {
      try {
        await deleteDoc(doc(db, 'batchDoubts', doubtId));
        toast.success('Doubt deleted successfully');
        await loadDoubts();
      } catch (error) {
        toast.error('Failed to delete doubt');
      }
    }
  };

  const handleFileUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      if (type === 'timetable') {
        setUploadingTimetable(true);
        const result = await uploadFile(file, `timetables/${batchId}`);
        if (result.success) {
          setTimetableFormData({ ...timetableFormData, pdfUrl: result.url });
          toast.success('File uploaded successfully');
        }
        setUploadingTimetable(false);
      } else if (type === 'result') {
        setUploadingResult(true);
        const result = await uploadFile(file, `results/${batchId}`);
        if (result.success) {
          setResultFormData({ ...resultFormData, documentUrl: result.url });
          toast.success('File uploaded successfully');
        }
        setUploadingResult(false);
      }
    } catch (error) {
      toast.error('Failed to upload file');
      setUploadingTimetable(false);
      setUploadingResult(false);
    }
  };

  const tabs = [
    { id: 'overview', name: 'Overview', icon: FileText },
    { id: 'students', name: 'Students', icon: Users, badge: enrolledStudents.length },
    { id: 'classes', name: 'Classes', icon: Video, badge: batchClasses.length },
    { id: 'announcements', name: 'Announcements', icon: Megaphone, badge: announcements.length },
    { id: 'doubts', name: 'Doubts', icon: MessageCircle, badge: doubts.length },
    { id: 'tests', name: 'Tests', icon: FileText, badge: tests.length },
    { id: 'schedule', name: 'Schedule', icon: Calendar, badge: timetables.length },
    { id: 'results', name: 'Results', icon: Award, badge: results.length },
  ];

  const formatSchedule = (schedule) => {
    if (!schedule || !schedule.days || schedule.days.length === 0) {
      return 'No schedule set';
    }
    return `${schedule.days.join(', ')} at ${schedule.time || 'TBD'}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!batch) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-600 dark:text-gray-400">Batch not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/coaching-admin/batches')}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                <ArrowLeft className="w-6 h-6 text-gray-600 dark:text-gray-400" />
              </button>
              <div>
                <div className="flex items-center space-x-3">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                    batch.isActive 
                      ? 'bg-blue-100 dark:bg-blue-900' 
                      : 'bg-gray-100 dark:bg-gray-700'
                  }`}>
                    <BookOpen className={`w-6 h-6 ${
                      batch.isActive 
                        ? 'text-blue-600 dark:text-blue-400' 
                        : 'text-gray-400'
                    }`} />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                      {batch.name}
                    </h1>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {batch.subject}
                    </p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    batch.isActive 
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                  }`}>
                    {batch.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>
            <button
              onClick={() => {
                // Navigate back to batch list - the edit functionality is in the original BatchManagement
                navigate('/coaching-admin/batches');
                toast.info('Please use the edit button on the batch card in the list');
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
            >
              <Edit className="w-4 h-4" />
              <span>Edit Batch</span>
            </button>
          </div>

          {/* Tabs */}
          <div className="mt-6 border-b border-gray-200 dark:border-gray-700">
            <nav className="flex space-x-8">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  >
                    <Icon className="w-5 h-5 mr-2" />
                    {tab.name}
                    {tab.badge !== undefined && (
                      <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                        activeTab === tab.id
                          ? 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-400'
                          : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                      }`}>
                        {tab.badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Enrolled Students</p>
                    <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                      {enrolledStudents.length}
                    </p>
                  </div>
                  <Users className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                </div>
              </div>
              
              <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Capacity</p>
                    <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                      {batch.capacity || 0}
                    </p>
                  </div>
                  <Users className="w-8 h-8 text-green-600 dark:text-green-400" />
                </div>
              </div>
              
              <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Fee</p>
                    <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                      ₹{batch.fee || 0}
                    </p>
                  </div>
                  <DollarSign className="w-8 h-8 text-purple-600 dark:text-purple-400" />
                </div>
              </div>
            </div>

            {/* Batch Details */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Batch Information
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start space-x-3">
                  <User className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Teacher</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {batch.teacherName || 'Not assigned'}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Schedule</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {formatSchedule(batch.schedule)}
                    </p>
                  </div>
                </div>
                
                {batch.location && (
                  <div className="flex items-start space-x-3">
                    <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Location</p>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {batch.location.room} {batch.location.building && `- ${batch.location.building}`}
                      </p>
                    </div>
                  </div>
                )}
                
                <div className="flex items-start space-x-3">
                  <Clock className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Duration</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {batch.duration || 'Not set'}
                    </p>
                  </div>
                </div>
              </div>

              {batch.description && (
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Description</p>
                  <p className="text-gray-900 dark:text-white">
                    {batch.description}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'students' && (
          <div className="space-y-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Enrolled Students ({enrolledStudents.length})
                </h3>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setShowStudentsModal(true)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
                  >
                    <Users className="w-4 h-4" />
                    <span>View All</span>
                  </button>
                  <button
                    onClick={() => setShowEnrollmentModal(true)}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center space-x-2"
                  >
                    <UserPlus className="w-4 h-4" />
                    <span>Enroll Students</span>
                  </button>
                </div>
              </div>

              {enrolledStudents.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    No students enrolled yet
                  </p>
                  <button
                    onClick={() => setShowEnrollmentModal(true)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Enroll First Student
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {enrolledStudents.slice(0, 9).map((student) => (
                    <div
                      key={student.id}
                      className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg"
                    >
                      <h4 className="font-medium text-gray-900 dark:text-white">
                        {student.name}
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {student.email}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {enrolledStudents.length > 9 && (
                <button
                  onClick={() => setShowStudentsModal(true)}
                  className="w-full mt-4 py-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg"
                >
                  View all {enrolledStudents.length} students
                </button>
              )}
            </div>
          </div>
        )}

        {activeTab === 'schedule' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Timetables ({timetables.length})
              </h3>
              <button
                onClick={() => {
                  setEditingTimetable(null);
                  setTimetableFormData({ title: '', description: '', pdfUrl: '' });
                  setShowTimetableModal(true);
                }}
                className="flex items-center space-x-1 px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm"
              >
                <Plus className="w-4 h-4" />
                <span>Add Timetable</span>
              </button>
            </div>
            {timetables.length === 0 ? (
              <p className="text-gray-600 dark:text-gray-400 text-center py-8">
                No timetables added yet
              </p>
            ) : (
              <div className="space-y-3">
                {timetables.map((timetable) => (
                  <div
                    key={timetable.id}
                    className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          {timetable.title}
                        </h4>
                        {timetable.description && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {timetable.description}
                          </p>
                        )}
                        {timetable.pdfUrl && (
                          <a
                            href={timetable.pdfUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-blue-600 hover:underline mt-2 inline-flex items-center gap-1"
                          >
                            <Download className="w-3 h-3" />
                            View PDF
                          </a>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => {
                            setEditingTimetable(timetable);
                            setTimetableFormData({
                              title: timetable.title || '',
                              description: timetable.description || '',
                              pdfUrl: timetable.pdfUrl || ''
                            });
                            setShowTimetableModal(true);
                          }}
                          className="p-2 text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteTimetable(timetable.id)}
                          className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'classes' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Classes ({batchClasses.length})
              </h3>
              <button
                onClick={() => {
                  setEditingClass(null);
                  setClassFormData({
                    subject: '',
                    date: '',
                    time: '',
                    duration: 60,
                    zoomLink: '',
                    status: 'scheduled',
                    description: ''
                  });
                  setShowClassModal(true);
                }}
                className="flex items-center space-x-1 px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm"
              >
                <Plus className="w-4 h-4" />
                <span>Add Class</span>
              </button>
            </div>
            {batchClasses.length === 0 ? (
              <p className="text-gray-600 dark:text-gray-400 text-center py-8">
                No classes added yet
              </p>
            ) : (
              <div className="space-y-3">
                {batchClasses.map((classItem) => (
                  <div
                    key={classItem.id}
                    className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          {classItem.subject}
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          {new Date(classItem.date).toLocaleDateString()} at {classItem.time}
                        </p>
                        {classItem.zoomLink && (
                          <a
                            href={classItem.zoomLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-blue-600 hover:underline mt-1 inline-block"
                          >
                            Join Class
                          </a>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          classItem.status === 'live' ? 'bg-green-100 text-green-800' :
                          classItem.status === 'completed' ? 'bg-gray-100 text-gray-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {classItem.status}
                        </span>
                        <button
                          onClick={() => {
                            setEditingClass(classItem);
                            setClassFormData({
                              subject: classItem.subject || '',
                              date: classItem.date || '',
                              time: classItem.time || '',
                              duration: classItem.duration || 60,
                              zoomLink: classItem.zoomLink || '',
                              status: classItem.status || 'scheduled',
                              description: classItem.description || ''
                            });
                            setShowClassModal(true);
                          }}
                          className="p-2 text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteClass(classItem.id)}
                          className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'announcements' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Announcements ({announcements.length})
              </h3>
              <button
                onClick={() => {
                  setEditingAnnouncement(null);
                  setAnnouncementFormData({ title: '', content: '' });
                  setShowAnnouncementModal(true);
                }}
                className="flex items-center space-x-1 px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm"
              >
                <Plus className="w-4 h-4" />
                <span>Add Announcement</span>
              </button>
            </div>
            {announcements.length === 0 ? (
              <p className="text-gray-600 dark:text-gray-400 text-center py-8">
                No announcements yet
              </p>
            ) : (
              <div className="space-y-3">
                {announcements.map((announcement) => (
                  <div
                    key={announcement.id}
                    className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          {announcement.title}
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 whitespace-pre-wrap">
                          {announcement.content}
                        </p>
                        <p className="text-xs text-gray-500 mt-2">
                          By {announcement.createdByName || 'Admin'} • {announcement.createdAt?.toDate?.()?.toLocaleDateString() || 'Recently'}
                        </p>
                      </div>
                      <div className="flex items-center space-x-2 ml-4">
                        <button
                          onClick={() => {
                            setEditingAnnouncement(announcement);
                            setAnnouncementFormData({
                              title: announcement.title,
                              content: announcement.content
                            });
                            setShowAnnouncementModal(true);
                          }}
                          className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteAnnouncement(announcement.id)}
                          className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'doubts' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Doubts ({doubts.length})
              </h3>
            </div>
            {doubts.length === 0 ? (
              <p className="text-gray-600 dark:text-gray-400 text-center py-8">
                No doubts yet
              </p>
            ) : (
              <div className="space-y-3">
                {doubts.map((doubt) => (
                  <div
                    key={doubt.id}
                    className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="text-xs px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded">
                            {doubt.subject || 'General'}
                          </span>
                          <span className={`text-xs px-2 py-1 rounded ${
                            doubt.status === 'answered' 
                              ? 'bg-green-100 text-green-800'
                              : doubt.status === 'closed'
                              ? 'bg-gray-100 text-gray-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {doubt.status || 'pending'}
                          </span>
                        </div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                          {doubt.question}
                        </p>
                        {doubt.answer && (
                          <div className="mt-2 p-2 bg-green-50 dark:bg-green-900/20 rounded border-l-4 border-green-500">
                            <p className="text-xs font-semibold text-green-800 dark:text-green-200 mb-1">Answer:</p>
                            <p className="text-sm text-gray-700 dark:text-gray-300">{doubt.answer}</p>
                          </div>
                        )}
                        <p className="text-xs text-gray-500 mt-2">
                          Asked by {doubt.askedByName || 'Student'} • {doubt.createdAt?.toDate?.()?.toLocaleDateString() || 'Recently'}
                        </p>
                      </div>
                      <div className="flex flex-col items-end space-y-2 ml-4">
                        {!doubt.answer && doubt.status !== 'closed' && (
                          <button
                            onClick={() => {
                              setAnsweringDoubt(doubt.id);
                              setDoubtAnswer('');
                              setShowDoubtModal(true);
                            }}
                            className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm"
                          >
                            Answer
                          </button>
                        )}
                        {doubt.status !== 'closed' && (
                          <button
                            onClick={() => handleCloseDoubt(doubt.id)}
                            className="px-3 py-1 bg-gray-600 hover:bg-gray-700 text-white rounded-lg text-sm"
                          >
                            Close
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteDoubt(doubt.id)}
                          className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'tests' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Tests ({tests.length})
              </h3>
              <button
                onClick={() => {
                  setTestFormData({ testId: '', testTitle: '' });
                  setShowTestModal(true);
                }}
                className="flex items-center space-x-1 px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm"
              >
                <Plus className="w-4 h-4" />
                <span>Add Test</span>
              </button>
            </div>
            {tests.length === 0 ? (
              <p className="text-gray-600 dark:text-gray-400 text-center py-8">
                No tests added yet
              </p>
            ) : (
              <div className="space-y-3">
                {tests.map((test) => (
                  <div
                    key={test.id}
                    className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          {test.testTitle || test.testId}
                        </h4>
                        <p className="text-xs text-gray-500 mt-1">
                          Test ID: {test.testId}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Added by {test.addedByName || 'Admin'} • {test.addedAt?.toDate?.()?.toLocaleDateString() || 'Recently'}
                        </p>
                      </div>
                      <button
                        onClick={() => handleDeleteTest(test.id)}
                        className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'results' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Results & Admit Cards ({results.length})
              </h3>
              <button
                onClick={() => {
                  setEditingResult(null);
                  setResultFormData({
                    studentEmail: '',
                    studentName: '',
                    examName: '',
                    examDate: '',
                    documentUrl: '',
                    documentType: 'result'
                  });
                  setShowResultModal(true);
                }}
                className="flex items-center space-x-1 px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm"
              >
                <Plus className="w-4 h-4" />
                <span>Add Result/Admit Card</span>
              </button>
            </div>
            {results.length === 0 ? (
              <p className="text-gray-600 dark:text-gray-400 text-center py-8">
                No results added yet
              </p>
            ) : (
              <div className="space-y-3">
                {results.map((result) => (
                  <div
                    key={result.id}
                    className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-white">
                          {result.examName}
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          Student: {result.studentName}
                        </p>
                        {result.examDate && (
                          <p className="text-xs text-gray-500 mt-1">
                            Date: {new Date(result.examDate).toLocaleDateString()}
                          </p>
                        )}
                        {result.documentUrl && (
                          <a
                            href={result.documentUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-blue-600 hover:underline mt-2 inline-flex items-center gap-1"
                          >
                            <Download className="w-3 h-3" />
                            View Document
                          </a>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => {
                            setEditingResult(result);
                            setResultFormData({
                              studentEmail: result.studentEmail || '',
                              studentName: result.studentName || '',
                              examName: result.examName || '',
                              examDate: result.examDate || '',
                              documentUrl: result.documentUrl || '',
                              documentType: result.documentType || 'result'
                            });
                            setShowResultModal(true);
                          }}
                          className="p-2 text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteResult(result.id)}
                          className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

      </div>

      {/* Modals */}
      {showStudentsModal && (
        <BatchEnrolledStudents
          batchId={batch.id}
          batchName={batch.name}
          onClose={() => {
            setShowStudentsModal(false);
            loadEnrolledStudents();
          }}
        />
      )}

      {showEnrollmentModal && (
        <BatchStudentEnrollment
          batchId={batch.id}
          coachingId={coachingId}
          onClose={() => {
            setShowEnrollmentModal(false);
            loadEnrolledStudents();
          }}
          onEnrollmentComplete={() => {
            loadEnrolledStudents();
            toast.success('Students enrolled successfully');
          }}
        />
      )}
    </div>
  );
};

export default BatchDetailsPage;
